/**
 * Calendar days for the filter bars' date ranges.
 *
 * The API takes plain `YYYY-MM-DD` days, and these helpers never go through a
 * UTC instant: "the 7th" means the 7th wherever the reader is. VentaHub turned
 * picked days into midnight-UTC instants and back, which moved both ends a day
 * early for anyone west of UTC.
 */

/** A local date as `YYYY-MM-DD`. */
export function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** `YYYY-MM-DD` as a local date at midnight. */
export function fromIsoDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1)
}

/** A day `days` after (or before, if negative) another. */
export function addDays(day: string, days: number): string {
  const date = fromIsoDay(day)
  date.setDate(date.getDate() + days)
  return isoDay(date)
}

export type DateRange = { from?: string; to?: string }

/**
 * The presets every date-range filter offers, in VentaHub's order: its
 * picker's chips — Last 7 · 15 · 30 · 60 · 90 days, This month, This year.
 */
export const DATE_PRESETS = [
  'last7',
  'last15',
  'last30',
  'last60',
  'last90',
  'thisMonth',
  'thisYear',
] as const
export type DatePreset = (typeof DATE_PRESETS)[number]

const LAST_DAYS: Partial<Record<DatePreset, number>> = {
  last7: 7,
  last15: 15,
  last30: 30,
  last60: 60,
  last90: 90,
}

/** The range a preset stands for, as of `today`; "This month" and "This year" run to today. */
export function presetRange(preset: DatePreset, today: Date = new Date()): Required<DateRange> {
  const end = isoDay(today)
  const days = LAST_DAYS[preset]
  if (days !== undefined) return { from: addDays(end, -(days - 1)), to: end }
  const year = today.getFullYear()
  return preset === 'thisMonth'
    ? { from: isoDay(new Date(year, today.getMonth(), 1)), to: end }
    : { from: isoDay(new Date(year, 0, 1)), to: end }
}

/** The preset a range is exactly equal to, if any. */
export function matchPreset(range: DateRange, today: Date = new Date()): DatePreset | undefined {
  if (!range.from || !range.to) return undefined
  return DATE_PRESETS.find((preset) => {
    const candidate = presetRange(preset, today)
    return candidate.from === range.from && candidate.to === range.to
  })
}

/** The first day of the month a day falls in, as `YYYY-MM-DD`. */
export function startOfMonth(day: string): string {
  const date = fromIsoDay(day)
  return isoDay(new Date(date.getFullYear(), date.getMonth(), 1))
}

/** The first day of the month `months` after (or before) the one a day falls in. */
export function addMonths(day: string, months: number): string {
  const date = fromIsoDay(day)
  return isoDay(new Date(date.getFullYear(), date.getMonth() + months, 1))
}

/** One cell of a month's grid. */
export type CalendarCell = { day: string; inMonth: boolean }

/**
 * A month as six weeks of seven days, Sunday first — the shape VentaHub's
 * calendar draws, so the grid never changes height between months. Days of
 * the neighbouring months fill the first and last weeks, marked `inMonth:
 * false`.
 */
export function monthGrid(month: string): CalendarCell[] {
  const first = fromIsoDay(startOfMonth(month))
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay())
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
    return { day: isoDay(date), inMonth: date.getMonth() === first.getMonth() }
  })
}

/** Whether `day` lies within `from`–`to`, both inclusive, either end open. */
export function inRange(day: string, from: string | undefined, to: string | undefined): boolean {
  if (!from || !to) return false
  return day >= from && day <= to
}

/**
 * A page's own preset chip for a date-range field: what it says and the range
 * it picks. Built by the page as of today; `DateRangeField` marks the chip
 * whose range equals the field's value.
 */
export type DateRangePreset = { id: string; label: string; range: Required<DateRange> }

/** 1 January of the year a day falls in. */
export function startOfYear(day: string): string {
  return `${day.slice(0, 4)}-01-01`
}

/** The first day of the calendar quarter a day falls in. */
export function startOfQuarter(day: string): string {
  const date = fromIsoDay(day)
  return isoDay(new Date(date.getFullYear(), date.getMonth() - (date.getMonth() % 3), 1))
}

/**
 * The same day of the month `months` later (earlier, if negative), clamped to
 * the last day of a shorter month — 31 March less one month is 28 (or 29)
 * February, as date-fns' `addMonths` / `subMonths` do (VentaHub's chart
 * presets, `pages/ams_view/components/TrendChart.tsx:224-275`).
 */
export function shiftMonths(day: string, months: number): string {
  const date = fromIsoDay(day)
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(date.getDate(), lastDay))
  return isoDay(target)
}

/**
 * The whole calendar month before the one `today` falls in — VentaHub's "Last
 * month" chip (`pages/ams_view/AmsViewPage.tsx:1830`,
 * `pages/review_ams_data/ReviewAmsDataPage.tsx:217-221`).
 */
export function lastMonth(today: Date): Required<DateRange> {
  const first = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const last = new Date(today.getFullYear(), today.getMonth(), 0)
  return { from: isoDay(first), to: isoDay(last) }
}
